import { getMediaFieldsFromAlertApiPayload } from "./alertApiPayloadMedia";
import {
  notifyChannelsFromAlertPayload,
  buildNotifyChannelItems,
} from "./alertNotifyChannels";
import { readSrIdFromUnknown } from "./srIdDisplay.ts";

/**
 * Helpers for GET /alerts/stream (SSE) — query building and mapping payloads
 * to the Alert Overview table row shape (see Dashboard.jsx buildTableRows).
 */

function formatAlertTypeLabel(raw) {
  if (!raw) return "—";
  return String(raw)
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Prefer `alert_type_name` (or camelCase) on the alert or inside `event_json`.
 * @param {Record<string, unknown>} alert
 * @returns {string|null}
 */
export function resolveAlertTypeNameRaw(alert) {
  if (!alert || typeof alert !== "object") return null;
  const eventJson =
    alert.event_json && typeof alert.event_json === "object"
      ? alert.event_json
      : {};
  const candidates = [
    alert.alert_type_name,
    alert.alertTypeName,
    eventJson.alert_type_name,
    eventJson.alertTypeName,
  ];
  for (const v of candidates) {
    if (v != null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return null;
}

/**
 * True when `alert_type` should be sent to GET /alerts/history and /alerts/stream as a
 * use-case UUID (unchanged). Matches AddAlertRule / mainAlertServices semantics.
 * @param {unknown} value
 */
export function isAlertHistoryUsecaseId(value) {
  const s = String(value ?? "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s,
  );
}

/**
 * @param {Record<string, unknown>} alert
 * @param {Record<string, unknown>} eventJson
 * @param {unknown[]} alertRules
 */
export function resolveDashboardRowUseCaseId(alert, eventJson, alertRules) {
  const fromPayload = [
    alert?.use_case_id,
    alert?.useCaseId,
    // History payloads often put the use-case UUID in `alert_type`.
    alert?.alert_type,
    alert?.alertType,
    eventJson?.use_case_id,
    eventJson?.useCaseId,
    eventJson?.alert_type,
    eventJson?.alertType,
  ];
  for (const c of fromPayload) {
    if (c != null && String(c).trim() !== "") {
      const s = String(c).trim();
      if (isAlertHistoryUsecaseId(s)) return s;
    }
  }
  const rid =
    alert?.rule_id != null && String(alert.rule_id).trim() !== ""
      ? String(alert.rule_id).trim()
      : "";
  if (!rid || !Array.isArray(alertRules)) return "";
  for (const rule of alertRules) {
    const apiId = rule?.apiId != null ? String(rule.apiId).trim() : "";
    const id = rule?.id != null ? String(rule.id).trim() : "";
    if (apiId === rid || id === rid) {
      const u = rule?.useCaseId != null ? String(rule.useCaseId).trim() : "";
      if (u && isAlertHistoryUsecaseId(u)) return u;
    }
  }
  return "";
}

function formatSeverityLabel(raw) {
  if (raw == null || raw === "") return "Medium";
  const s = String(raw).toLowerCase().replace(/_/g, " ").trim();
  const map = {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
  };
  if (map[s]) return map[s];
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Workspace / lifecycle status only (what the operator did with the alert).
 * Uses `status` / `alert_status` / `event_json.status` — never `notification_status`.
 *
 * Active = open; Acknowledged / Closed = user progress on the alert.
 * Notification delivery (email sent, failed, pending) is separate; see row
 * `notificationStatus` and API `notification_status`.
 */
function deriveWorkspaceStatus(apiAlert, eventJson) {
  const s = (
    apiAlert.status ||
    apiAlert.alert_status ||
    eventJson.status ||
    ""
  )
    .toString()
    .toLowerCase();
  if (s.includes("ack")) return "Acknowledged";
  if (s.includes("close") || s.includes("resolved")) return "Closed";
  return "Active";
}

function ruleChannelsForRow(alertRules, ruleId) {
  if (ruleId == null || ruleId === "") {
    return [
      { label: "SMS", enabled: false },
      { label: "Email", enabled: true },
      { label: "Telegram", enabled: true },
    ];
  }
  const rid = String(ruleId);
  const rule = alertRules?.find(
    (r) => String(r.id) === rid || String(r.apiId) === rid,
  );
  const types = rule?.actionTypes?.length
    ? rule.actionTypes
    : ["Email", "SMS", "Telegram"];
  return ["SMS", "Email", "Telegram"].map((ch) => ({
    label: ch,
    enabled: types.some((t) => String(t).toLowerCase() === ch.toLowerCase()),
  }));
}

/**
 * Display value for the Notified to column (history, dashboard, SSE rows).
 * Prefers backend `notify_to`, then `notified_to` / camelCase, then `event_json`.
 *
 * @param {Record<string, unknown>} alert
 * @returns {string}
 */
export function formatAlertNotifiedToDisplay(alert) {
  if (!alert || typeof alert !== "object") return "—";
  const eventJson =
    alert.event_json && typeof alert.event_json === "object"
      ? alert.event_json
      : {};

  const raw =
    alert.notify_to ??
    alert.notifyTo ??
    alert.notified_to ??
    alert.notifiedTo ??
    eventJson.notify_to ??
    eventJson.notifyTo ??
    eventJson.notified_to ??
    eventJson.notifiedTo ??
    eventJson.recipients;

  if (Array.isArray(raw)) {
    const joined = raw
      .map((x) => (x == null ? "" : String(x)))
      .filter(Boolean)
      .join(", ");
    return joined || "—";
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(raw);
  }
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return "—";
}

const DEFAULT_THUMB =
  "https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=80&h=60&fit=crop";

/**
 * Parse alert API datetimes for display/sort. GET /alerts/history often returns UTC
 * wall times without `Z`; `new Date("...T...")` treats those as *local* and shifts
 * labels vs SSE/Dashboard payloads that include `Z` or offsets.
 * @param {unknown} raw
 * @returns {Date}
 */
export function parseAlertApiDateTime(raw) {
  if (raw == null || raw === "") return new Date(NaN);
  if (raw instanceof Date) return new Date(raw.getTime());
  const s0 = String(raw).trim();
  if (!s0 || s0.toLowerCase() === "null") return new Date(NaN);
  const sNorm = s0.includes("T") ? s0 : s0.replace(" ", "T");
  const hasOffset =
    /[zZ]\s*$/.test(sNorm) ||
    /[+-]\d{2}:\d{2}(?::\d{2})?\s*$/.test(sNorm) ||
    /[+-]\d{4}\s*$/.test(sNorm);
  const isoNaiveUtc = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d{1,9})?$/;
  if (!hasOffset && isoNaiveUtc.test(sNorm)) {
    return new Date(`${sNorm}Z`);
  }
  return new Date(sNorm);
}

function resolveAlertMediaUrl(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (!s || s.toLowerCase() === "null") return "";
  if (/^https?:\/\//i.test(s)) return s;
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  if (!base) return s.startsWith("/") ? s : `/${s}`;
  return `${base}/${s.replace(/^\//, "")}`;
}

/**
 * Map one AlertResponse-like object (history / SSE) to a Dashboard table row.
 * @param {Record<string, unknown>} alert
 * @param {Array<Record<string, unknown>>} [alertRules]
 * @param {Map<string, string>|undefined} [sourceIdToDisplayName] - GET /cameras id → name when `source_name` is missing
 * @returns {Record<string, unknown> | null}
 */
export function mapAlertResponseToDashboardRow(
  alert,
  alertRules = [],
  sourceIdToDisplayName,
) {
  if (!alert || typeof alert !== "object") return null;
  const eventJson =
    alert.event_json && typeof alert.event_json === "object"
      ? alert.event_json
      : {};
  const id =
    alert.id != null
      ? String(alert.id)
      : alert._id != null
        ? String(alert._id)
        : null;
  if (!id) return null;

  const detBlock = alert.detections;

  const imageUrl =
    eventJson.image_url ||
    alert.photo_url ||
    alert.thumbnail_url ||
    alert.thumbnailUrl ||
    alert.image_url ||
    alert.image ||
    alert.thumbnail ||
    "";

  const alertName = alert.alert_name || alert.message || "Alert";
  const typeNameRaw = resolveAlertTypeNameRaw(alert);
  const alertType = typeNameRaw
    ? typeNameRaw
    : formatAlertTypeLabel(alert.alert_type || eventJson.alert_type);

  const sidRaw =
    alert.source_id != null && String(alert.source_id).trim() !== ""
      ? String(alert.source_id).trim()
      : "";
  let sourceName =
    (alert.source_name != null && String(alert.source_name).trim() !== ""
      ? String(alert.source_name).trim()
      : "") ||
    (alert.sourceName != null && String(alert.sourceName).trim() !== ""
      ? String(alert.sourceName).trim()
      : "");
  if (
    !sourceName &&
    sidRaw &&
    sourceIdToDisplayName &&
    typeof sourceIdToDisplayName.get === "function"
  ) {
    const resolved = sourceIdToDisplayName.get(sidRaw);
    if (resolved) sourceName = resolved;
  }
  if (!sourceName) {
    sourceName = sidRaw ? "Unknown source" : "—";
  }

  const zoneRaw =
    alert.zone_name ||
    alert.zoneName ||
    null;
  const zone = zoneRaw != null && zoneRaw !== "" ? zoneRaw : "—";

  const ts =
    eventJson.created_at ||
    (detBlock && typeof detBlock === "object" && detBlock.created_at
      ? detBlock.created_at
      : null) ||
    alert.created_at ||
    alert.timestamp ||
    new Date().toISOString();
  const at = parseAlertApiDateTime(ts);

  const severity = formatSeverityLabel(
    alert.severity || alert.rule_severity || "medium",
  );
  const status = deriveWorkspaceStatus(alert, eventJson);

  const shortName =
    alertName && String(alertName).trim()
      ? String(alertName).trim().split(/\s+/)[0]
      : "Alert";

  const messageForSearch =
    (typeof alert.message === "string" && alert.message.trim()) ||
    (typeof eventJson.message === "string" && eventJson.message.trim()) ||
    "";

  const media = getMediaFieldsFromAlertApiPayload(alert);
  const thumb =
    media.photoUrl ||
    media.thumbnailUrl ||
    resolveAlertMediaUrl(imageUrl) ||
    imageUrl;

  const notifyChannels = notifyChannelsFromAlertPayload(alert);

  const srId =
    readSrIdFromUnknown(alert) ?? readSrIdFromUnknown(eventJson);

  return {
    rowKey: id,
    /** Set in UI from paginated row index via alertServiceSequentialDisplayId("AL", …). */
    displayId: "",
    alertName: shortName,
    alertNameFull: String(alertName).trim() || "Alert",
    severity,
    at: Number.isNaN(at.getTime()) ? new Date() : at,
    /** Use-case UUID for server-side `alert_type` filter (when known). */
    useCaseId: resolveDashboardRowUseCaseId(alert, eventJson, alertRules),
    alertType,
    sourceName,
    zone,
    status,
    notificationStatus:
      alert.notification_status != null && String(alert.notification_status).trim() !== ""
        ? String(alert.notification_status).trim()
        : eventJson.notification_status != null && String(eventJson.notification_status).trim() !== ""
          ? String(eventJson.notification_status).trim()
          : "",
    notifiedTo: formatAlertNotifiedToDisplay(alert),
    channels: ruleChannelsForRow(alertRules, alert.rule_id),
    /** Per-alert channels for "Notified by" chips (API), not the rule template in `channels`. */
    notifyChannels,
    /** Per-channel delivery outcome for chip styling (green vs red). */
    notifyChannelItems: buildNotifyChannelItems(alert),
    thumb,
    photoUrl: media.photoUrl,
    thumbnailCropUrl: media.thumbnailCropUrl,
    videoUrl: media.videoUrl || undefined,
    detectionId: media.detectionId,
    hasVideo: media.hasVideo,
    hasVideoFromApi: media.hasVideoFromApi,
    /** Extra text for dashboard / stream search (not shown as a column). */
    message: messageForSearch,
    ruleId:
      alert.rule_id != null && alert.rule_id !== ""
        ? String(alert.rule_id)
        : "",
    sourceId: sidRaw,
    roiId:
      alert.roi_id != null && alert.roi_id !== ""
        ? String(alert.roi_id)
        : alert.roiId != null && alert.roiId !== ""
          ? String(alert.roiId)
          : eventJson.roi_id != null && eventJson.roi_id !== ""
            ? String(eventJson.roi_id)
            : eventJson.roiId != null && eventJson.roiId !== ""
              ? String(eventJson.roiId)
              : "",
    /** Backend serial id (`sr_id`) for ID column when present */
    srId,
    _fromStream: true,
  };
}

/**
 * Normalize GET /alerts/history JSON (`{ success, message, data: { alerts, total, page_size } }`
 * and variants) for Dashboard + Alert History.
 *
 * @param {unknown} response
 * @returns {{ alertsArray: unknown[]; total: number; responseData: Record<string, unknown> }}
 */
export function extractAlertsHistoryPayload(response) {
  if (response == null || typeof response !== "object") {
    return { alertsArray: [], total: 0, responseData: {} };
  }
  const root = /** @type {Record<string, unknown>} */ (response);
  const dataLayer =
    root.data != null &&
    typeof root.data === "object" &&
    !Array.isArray(root.data)
      ? /** @type {Record<string, unknown>} */ (root.data)
      : null;

  let alertsArray = [];
  if (dataLayer && Array.isArray(dataLayer.alerts)) {
    alertsArray = dataLayer.alerts;
  } else if (dataLayer && Array.isArray(dataLayer.results)) {
    alertsArray = dataLayer.results;
  } else if (Array.isArray(root.alerts)) {
    alertsArray = root.alerts;
  } else if (Array.isArray(root.data)) {
    alertsArray = root.data;
  } else if (dataLayer && Array.isArray(dataLayer.data)) {
    alertsArray = dataLayer.data;
  } else if (Array.isArray(response)) {
    alertsArray = /** @type {unknown[]} */ (response);
  }

  const totalRaw =
    (dataLayer && dataLayer.total !== undefined ? dataLayer.total : undefined) ??
    (dataLayer && dataLayer.total_count !== undefined
      ? dataLayer.total_count
      : undefined) ??
    root.total;

  let total =
    typeof totalRaw === "number" && !Number.isNaN(totalRaw)
      ? totalRaw
      : Number.parseInt(String(totalRaw ?? ""), 10);
  if (Number.isNaN(total)) total = alertsArray.length;

  const responseData =
    dataLayer && typeof dataLayer === "object" ? dataLayer : root;

  return { alertsArray, total, responseData };
}

/**
 * Merge stream SSE payload onto REST baseline without letting `undefined` erase existing keys.
 * @param {Record<string, unknown>|null|undefined} prev
 * @param {Record<string, unknown>|null|undefined} payload
 */
export function mergeStreamAlertWithBaseline(prev, payload) {
  if (!prev || typeof prev !== "object") return payload;
  if (!payload || typeof payload !== "object") return prev;
  const out = { ...prev };
  for (const [k, v] of Object.entries(payload)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

function appendCsvParam(params, key, value) {
  if (value == null || value === "") return;
  if (Array.isArray(value)) {
    const s = value.map(String).filter(Boolean).join(",");
    if (s) params.append(key, s);
    return;
  }
  params.append(key, String(value));
}

/**
 * Build URLSearchParams for GET /alerts/stream (same semantics as history).
 * @param {Record<string, unknown>} filters
 * @returns {URLSearchParams}
 */
export function buildAlertsStreamSearchParams(filters) {
  const p = new URLSearchParams();
  if (!filters || typeof filters !== "object") return p;

  appendCsvParam(p, "source_id", filters.source_id);
  appendCsvParam(p, "source_name", filters.source_name);
  appendCsvParam(p, "rule_id", filters.rule_id);
  appendCsvParam(p, "severity", filters.severity);
  appendCsvParam(p, "status", filters.status);
  appendCsvParam(p, "notification_status", filters.notification_status);
  appendCsvParam(p, "notify_by", filters.notify_by);
  if (filters.alert_name) {
    p.append("alert_name", String(filters.alert_name));
  }
  appendCsvParam(p, "roi_id", filters.roi_id);
  if (filters.source_type) {
    p.append("source_type", String(filters.source_type));
  }
  appendCsvParam(p, "alert_type", filters.alert_type);
  if (filters.period) {
    p.append("period", String(filters.period));
  }
  if (filters.start_date) {
    p.append("start_date", String(filters.start_date));
  }
  if (filters.end_date) {
    p.append("end_date", String(filters.end_date));
  }
  const search =
    typeof filters.search === "string" ? filters.search.trim() : "";
  if (search.length >= 2) {
    p.append("search", search);
  }

  return p;
}
